import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useRolePerms } from '../features/auth/useRolePerms.js';
import { downloadCSV } from '../lib/csv.js';
import { useResetSub } from '../features/filters/useFilters.js';

interface AvailabilitySignal {
  id: string;
  workerId: string;
  workerName: string;
  workerRole: string | null;
  signaledAt: string;
  source: string | null;
  wasDeployedOn: string[];
}

function initials(name: string): string {
  return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
}

function avEl(name: string, size = 28, font = 10): JSX.Element {
  return (
    <div style={{ width: size, height: size, minWidth: size, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: font, fontFamily: 'Syne, sans-serif', fontWeight: 800, color: '#0B0F1A', flexShrink: 0 }}>
      {initials(name)}
    </div>
  );
}

function signalAge(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export function AvailabilitySignalsPage() {
  const { canExport } = useRolePerms();
  const navigate = useNavigate();
  const [window, setWindow] = useState(72);

  useResetSub(() => { setWindow(72); });

  const { data: signals = [] } = useQuery({
    queryKey: ['availability-signals'],
    queryFn: () => api.get<AvailabilitySignal[]>('/availability-signals').then((r) => r.data),
  });

  const filtered = useMemo(() => {
    if (window === 0) return signals;
    const cutoff = Date.now() - window * 3_600_000;
    return signals.filter((s) => new Date(s.signaledAt).getTime() >= cutoff);
  }, [signals, window]);

  const total = signals.length;
  const last24h = signals.filter((s) => Date.now() - new Date(s.signaledAt).getTime() < 86_400_000).length;
  const last7d = signals.filter((s) => Date.now() - new Date(s.signaledAt).getTime() < 7 * 86_400_000).length;
  const uniqueWorkers = new Set(signals.map((s) => s.workerId)).size;

  const kpiTiles = [
    { label: 'Total Signals', val: total, sub: 'all time', color: '#3B82F6' },
    { label: 'Last 24h', val: last24h, sub: 'recent activity', color: '#22C55E' },
    { label: 'Last 7 Days', val: last7d, sub: 'weekly signals', color: '#E8A427' },
    { label: 'Unique Workers', val: uniqueWorkers, sub: 'self-reporting', color: '#A855F7' },
  ];

  return (
    <div className="page" id="page-availsignals">
      <div className="hbar">
        <div>
          <div className="ph">🟢 Availability Signals</div>
          <div className="ps">Workers who have self-reported availability. Recent signals highlighted.</div>
        </div>
      </div>
      <div className="g4" style={{ marginBottom: 16 }} id="avail-kpis">
        {kpiTiles.map((k) => (
          <div key={k.label} className="kpi" style={{ borderTopColor: k.color }}>
            <div className="lbl">{k.label}</div>
            <div className="val" style={{ color: k.color }}>{k.val}</div>
            <div className="sub" style={{ color: k.color, opacity: .7 }}>{k.sub}</div>
          </div>
        ))}
      </div>
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
          <div className="sec-title" style={{ margin: 0 }}>Signal History</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <select
              id="avail-filter-window"
              value={window}
              onChange={(e) => setWindow(parseInt(e.target.value, 10))}
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontFamily: 'DM Sans, sans-serif', fontSize: 11, padding: '5px 9px' }}
            >
              <option value={24}>Last 24 hours</option>
              <option value={72}>Last 72 hours</option>
              <option value={168}>Last 7 days</option>
              <option value={720}>Last 30 days</option>
              <option value={0}>All time</option>
            </select>
            {canExport ? <button className="btn btn-ghost btn-sm" onClick={() => downloadCSV('availability-signals.csv', filtered.map((s) => ({
              worker: s.workerName,
              role: s.workerRole ?? '',
              signaledAt: new Date(s.signaledAt).toISOString(),
              source: s.source ?? '',
              wasDeployedOn: s.wasDeployedOn.join('; '),
            })))}>⬇ Export</button> : null}
          </div>
        </div>
        <div id="avail-list">
          {filtered.length === 0 ? (
            <div style={{ color: 'var(--muted)', fontSize: 12, textAlign: 'center', padding: 20 }}>No signals in this window.</div>
          ) : (
            filtered.map((s) => (
              <div
                key={s.id}
                style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 6px', borderBottom: '1px solid rgba(30,42,64,.4)', cursor: 'pointer' }}
                onClick={() => navigate(`/workers?id=${s.workerId}`)}
              >
                {avEl(s.workerName, 28, 10)}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{s.workerName}</div>
                  <div style={{ fontSize: 10, color: 'var(--muted)' }}>
                    {s.workerRole ?? ''}
                    {s.wasDeployedOn && s.wasDeployedOn.length ? ` · was on ${s.wasDeployedOn.join(', ')}` : ''}
                    {s.source ? ` · via ${s.source}` : ''}
                  </div>
                </div>
                <span style={{ fontSize: 10, color: '#22c55e', fontWeight: 600 }}>{signalAge(s.signaledAt)}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
